import type { Query } from 'sift'
import sift from 'sift'
import orderBy from 'lodash/orderBy'
import _filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import isNil from 'lodash/isNil'

type SortDirection = 1 | -1
type Sort<T> = Partial<Record<keyof T, SortDirection>>

type QueryOptions<T> = {
  limit?: number
  skip?: number
  sort?: Sort<T>
}

/**
 * Get the local list and using siftjs we will do filter, sort, limit and skip for the list
 * @param list - the list that we are working on
 * @param filter - the mongodb style filter
 * @param options - options for filter
 */
export function getLocalList<T>(list: T[], filter: Query<T>, options: QueryOptions<T>): T[] {
  //if the list is empty, return empty array
  if (isEmpty(list)) {
    return []
  }

  // 1) Filter
  let filteredList: T[]

  if (isEmpty(filter)) {
    filteredList = list.slice()
  } else {
    filteredList = _filter<T>(list, sift(filter))
  }

  // 2) Sort - MongoDB-style object syntax only
  //    { fieldA: 1|-1|{ $meta: 'textScore' }, fieldB: 1|-1, ... }
  const sort = options?.sort

  let sortedList = filteredList
  if (sort && typeof sort === 'object' && !Array.isArray(sort)) {
    const iteratees: Array<string> = []
    const orders: Array<'asc' | 'desc'> = []

    for (const key of Object.keys(sort)) {
      const dir = sort[key]
      // Ignore $meta as we cannot compute textScore locally
      if (dir && typeof dir === 'object' && '$meta' in dir) {
        continue
      }
      iteratees.push(key)
      orders.push(dir === -1 ? 'desc' : 'asc')
    }

    if (!isEmpty(iteratees)) {
      sortedList = orderBy<T>(filteredList, iteratees, orders)
    }
  }

  // 3) Pagination - no defaults.
  //    - If skip is undefined/null/negative values => 0
  //    - If limit is undefined/null or 0 or -1 => get all from skip
  const rawSkip = options?.skip
  let skip = isNil(rawSkip) ? 0 : toNumber(rawSkip)
  if (skip < 0) skip = 0 //clamp to zero if negative values

  const rawLimit = options?.limit
  let limit = isNil(rawLimit) ? 0 : toNumber(rawLimit)
  if (limit < 0) limit = 0 //clamp to zero if negative values

  let paginatedList: T[]
  if (limit === 0) {
    // get all from skip
    paginatedList = sortedList.slice(skip)
  } else {
    paginatedList = sortedList.slice(skip, skip + limit)
  }

  return paginatedList
}
